'use client';

import { ChevronLeftIcon, ChevronRightIcon } from 'tdesign-icons-react';
import { usePage } from '@ssv-website/hooks';
import {
  useSpring,
  animated,
  config,
  useSpringRef,
  useSpringValue,
} from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
export const ScrollPage = forwardRef(
  (
    {
      page = 1,
      total = 0,
      size = 1,
      className = '',
      width = '50%',
      onChange = (page: number) => void 0,
      onScrollChange = (page: number) => void 0,
    }: {
      page?: number;
      total: number;
      size?: number;
      className?: string;
      width?: string | number;
      onChange?(page: number): void;
      onScrollChange?(
        currentRange: number,
        ranges: [range1: number, range2: number]
      ): void;
    },
    ref
  ) => {
    const {
      totalPage,
      enableNext,
      enablePrev,
      onPrev,
      onNext,
      current,
      ...scrollPage
    } = usePage({
      total,
      page,
      pageSize: size,
      pageFrom: 0,
    });
    const refNode = useRef<HTMLDivElement>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const widthRef = useRef(0);
    const springRef = useSpringRef();
    const percent = totalPage ? (100 / totalPage)?.toFixed(0) : 100;
    const springValue = useSpringValue(0);
    const styles = useSpring({
      ref: springRef,
      x: current * widthRef.current,
      config: config.molasses,
    });

    const bind = useDrag(
      ({ active, offset: [ox] }) => {
        if (active || typeof onScrollChange === 'function') {
          const min = 0;
          //避免超出边界
          const max = (parentRef.current?.clientWidth || 0) - widthRef.current;
          springRef.start({ x: ox < min ? min : ox > max ? max : ox });
          onScrollChange?.(ox, [0, parentRef.current?.clientWidth || 0]);
        } else {
          // 四舍五入后取天花板
          const value = Math.ceil(Math.round(ox / widthRef.current)).toFixed(0);
          if (Number(value) === current) {
            // 回退到原始位置
            springRef.start({ x: current * widthRef.current });
          } else {
            scrollPage.onChange(Number(value));
          }
        }
      },
      { filterTaps: true, bounds: parentRef, axis: 'x' } // 过滤点击事件
    );

    useImperativeHandle(ref, () => ({
      scrollTo(
        currentRange: number,
        ranges: [range1: number, range2: number],
        active: false
      ) {
        springValue.set(currentRange);
        // 滚动和滑块映射
        const interpolations = springValue
          .to(ranges, [
            0,
            (parentRef.current?.clientWidth || 0) - widthRef.current,
          ])
          .to((x) => x)
          .get();
        springRef.start({ x: interpolations });
        // 拖拽才需要动画
        if (active) {
          const value = Math.ceil(
            Math.round(interpolations / widthRef.current)
          ).toFixed(0);
          scrollPage.onChange(Number(value));
        }
      },
    }));

    useEffect(() => {
      widthRef.current = refNode.current?.getBoundingClientRect().width || 0;
      const handleResize = () => {
        widthRef.current = refNode.current?.getBoundingClientRect().width || 0;
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
      springRef.start();
      onChange?.(current);
    }, [current]);

    return (
      <div className={`flex justify-center ${className}`}>
        <div className="flex items-center" style={{ width }}>
          <div
            className={`rounded-full bg-transparent flex items-center justify-center p-[7px] ${
              enablePrev ? 'cursor-pointer bg-white' : 'cursor-not-allowed'
            } `}
            onClick={onPrev}
          >
            <ChevronLeftIcon
              name="chevron-left"
              color={`rgba(53,63,73,${enablePrev ? '1' : '.6'})`}
            />
          </div>
          <div
            className="h-1 w-full bg-primary/10 rounded-full mx-5 relative"
            ref={parentRef}
          >
            <animated.div
              className="h-full bg-primary rounded-full absolute left-0 top-0 touch-pan-y"
              ref={refNode}
              style={{
                width: percent + '%',
                ...styles,
                cursor: 'grab',
              }}
              {...bind()}
            />
          </div>
          <div
            className={`rounded-full bg-transparent flex items-center justify-center p-[7px] ${
              enableNext ? 'cursor-pointer bg-white' : 'cursor-not-allowed'
            }`}
            onClick={onNext}
          >
            <ChevronRightIcon
              name="chevron-right"
              color={`rgba(53,63,73,${enableNext ? '1' : '.6'})`}
            />
          </div>
        </div>
      </div>
    );
  }
);
